import {AlertIcon, ChevronDownIcon, ChevronRightIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, Heading, IconButton, Text} from '@primer/react'
import {clsx} from 'clsx'
import {
  type ComponentProps,
  type HTMLAttributes,
  memo,
  type MouseEvent,
  type Ref,
  type RefObject,
  useId,
  useRef,
} from 'react'

import lineNumberStyles from '../blob/BlobContent/Code/LineNumber.module.css'
import styles from './TaillightsGutterRow.module.css'

export type TaillightsGutterAction = {id: string; label: string} & (
  | {kind: 'local' | 'fixture'; onSelect: () => void}
  | {kind: 'unavailable'; reason: string}
  | {kind: 'link'; href: string}
)

export type TaillightsGutterAnnotation = {
  kind: 'unicode' | 'codeowners'
  message: string
}

export interface TaillightsGutterRowProps {
  /** Original, 1-based source identity; never the projected row index. */
  lineNumber: number
  top: number
  /** A 1-based row in the host grid; omitting it retains the standalone row wrapper. */
  gridRow?: number
  /** Must match the fixed 20px source row pitch; other heights are rejected, not clamped. */
  height: number
  edge?: 'start' | 'end'
  selected?: boolean
  rangeStart?: boolean
  /** Only one projected row should participate in sequential keyboard navigation. */
  tabStop?: boolean
  lane: 0 | 1
  fold?: {startLine: number; collapsed: boolean}
  annotations?: readonly TaillightsGutterAnnotation[]
  menuOpen: boolean
  showLineActions?: boolean
  annotationOpen?: boolean
  actions: readonly TaillightsGutterAction[]
  menuLabel?: string
  ref?: Ref<HTMLAnchorElement>
  onLine: (lineNumber: number, extend: boolean) => void
  onToggle: (startLine: number) => void
  onSnapshot: () => void
  onMenuOpenChange: (lineNumber: number, open: boolean, closeReason?: 'tab') => void
  onAnnotationOpenChange: (lineNumber: number, open: boolean) => void
  onFocusChange: (lineNumber: number | null) => void
}

type GutterControlProps = Pick<
  HTMLAttributes<HTMLElement>,
  'onPointerDownCapture' | 'onKeyDownCapture' | 'onFocus' | 'onBlur'
> & {'data-gutter-line': number}

function isModified(event: MouseEvent) {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native link navigation, not a shortcut.
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey
}

function MenuAnchor({tabStop, ...props}: ComponentProps<typeof IconButton> & {tabStop: boolean}) {
  // AnchoredOverlay supplies tabIndex=0; the adapter, not each mounted row, owns the tab stop.
  return <IconButton {...props} tabIndex={tabStop ? 0 : -1} />
}

/** Renders a 20px source-line gutter with caller-controlled selection, folding, actions and annotation details. */
export const TaillightsGutterRow = memo(function GutterRow({
  lineNumber,
  top,
  gridRow,
  height,
  edge,
  selected = false,
  rangeStart = false,
  tabStop = false,
  lane,
  fold,
  annotations,
  menuOpen,
  showLineActions = true,
  annotationOpen = false,
  actions,
  menuLabel,
  ref,
  onLine,
  onToggle,
  onSnapshot,
  onMenuOpenChange,
  onAnnotationOpenChange,
  onFocusChange,
}: TaillightsGutterRowProps) {
  if (height !== 20) throw new RangeError('TaillightsGutterRow requires a 20px source row height')
  if (gridRow !== undefined && (!Number.isInteger(gridRow) || gridRow < 1)) {
    throw new RangeError('TaillightsGutterRow requires a positive integer grid row')
  }

  const menuTabEventRef = useRef<KeyboardEvent | null>(null)
  const stateId = useId()
  const tabIndex = tabStop ? 0 : -1
  // Empty rows keep the host's action-strip layout, but do not render a menu.
  const hasLineActions = showLineActions && actions.length > 0
  const interactionProps: GutterControlProps = {
    'data-gutter-line': lineNumber,
    onPointerDownCapture: onSnapshot,
    onKeyDownCapture: event => {
      onSnapshot()
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Observe Primer's Tab dismissal without handling or preventing the key.
      menuTabEventRef.current = menuOpen && event.key === 'Tab' ? event.nativeEvent : null
    },
    onFocus: () => onFocusChange(lineNumber),
    onBlur: event => {
      if (event.currentTarget.contains(event.relatedTarget)) return
      const next = event.relatedTarget instanceof Element ? event.relatedTarget.closest('[data-gutter-line]') : null
      if (
        gridRow !== undefined &&
        next?.getAttribute('data-gutter-line') === String(lineNumber) &&
        next.parentElement === event.currentTarget.parentElement
      ) {
        return
      }
      onFocusChange(null)
    },
  }
  const controlProps = gridRow === undefined ? undefined : interactionProps
  const gridStyle = gridRow === undefined ? undefined : {gridRow: `${gridRow} / span 1`}
  const content = (
    <>
      {hasLineActions && (
        <GutterMenu
          lineNumber={lineNumber}
          lane={lane}
          tabStop={tabStop}
          menuOpen={menuOpen}
          actions={actions}
          menuLabel={menuLabel}
          onMenuOpenChange={onMenuOpenChange}
          tabEventRef={menuTabEventRef}
          gridRow={gridRow}
          edge={edge}
          controlProps={controlProps}
        />
      )}
      {!!annotations?.length && (
        <GutterAnnotations
          lineNumber={lineNumber}
          tabStop={tabStop}
          annotations={annotations}
          annotationOpen={annotationOpen}
          onAnnotationOpenChange={onAnnotationOpenChange}
          gridRow={gridRow}
          showLineActions={showLineActions}
          controlProps={controlProps}
        />
      )}
      <a
        {...controlProps}
        ref={ref}
        className={clsx(styles.LineLink, gridRow !== undefined && styles.GridLink, selected && styles.selected)}
        style={gridStyle}
        data-line-actions={showLineActions}
        href={`#L${lineNumber}`}
        tabIndex={tabIndex}
        aria-label={`Line ${lineNumber}`}
        aria-describedby={selected ? stateId : undefined}
        onClick={event => {
          if (isModified(event)) return
          event.preventDefault()
          onLine(lineNumber, event.shiftKey)
        }}
      >
        {lineNumber}
      </a>
      {fold && (
        <span
          {...controlProps}
          className={clsx(
            styles.Fold,
            lineNumberStyles.codeAlert,
            lineNumberStyles.codeAlertRight,
            gridRow !== undefined && styles.GridFold,
          )}
          style={{...gridStyle, lineHeight: 'inherit', marginBlock: 0}}
        >
          <button
            type="button"
            tabIndex={tabIndex}
            className={clsx(lineNumberStyles.codeFoldingChevron, gridRow !== undefined && styles.GridFoldButton)}
            aria-label={`${fold.collapsed ? 'Expand' : 'Collapse'} section at line ${fold.startLine}`}
            aria-expanded={!fold.collapsed}
            onClick={() => onToggle(fold.startLine)}
          >
            {fold.collapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
          </button>
        </span>
      )}
      {selected && (
        <span id={stateId} className={styles.SrOnly}>
          {rangeStart ? 'Start of selected range' : 'Selected line'}
        </span>
      )}
    </>
  )
  if (gridRow !== undefined) return content
  return (
    <div
      {...interactionProps}
      className={clsx(styles.Row, selected && styles.selected)}
      data-edge={edge}
      data-line-actions={showLineActions}
      style={{top, height, lineHeight: `${height}px`, padding: 0, cursor: 'default'}}
    >
      {content}
    </div>
  )
})

function GutterMenu({
  lineNumber,
  lane,
  tabStop = false,
  menuOpen,
  actions,
  menuLabel: providedMenuLabel,
  onMenuOpenChange,
  tabEventRef,
  gridRow,
  edge,
  controlProps,
}: Pick<
  TaillightsGutterRowProps,
  'lineNumber' | 'lane' | 'tabStop' | 'menuOpen' | 'actions' | 'menuLabel' | 'onMenuOpenChange' | 'gridRow' | 'edge'
> & {tabEventRef: RefObject<KeyboardEvent | null>; controlProps?: GutterControlProps}) {
  const menuRef = useRef<HTMLButtonElement>(null)
  const menuLabel = providedMenuLabel === undefined ? `Line ${lineNumber} actions` : providedMenuLabel
  return (
    <ActionMenu
      open={menuOpen}
      anchorRef={menuRef}
      onOpenChange={open => {
        const tabEvent = tabEventRef.current
        // Primer exposes only a boolean. An active Tab event distinguishes navigation from later dismissals.
        const closeReason = !open && tabEvent && tabEvent.eventPhase !== Event.NONE ? 'tab' : undefined
        tabEventRef.current = null
        onMenuOpenChange(lineNumber, open, closeReason)
      }}
    >
      <ActionMenu.Anchor>
        <MenuAnchor
          {...controlProps}
          icon={KebabHorizontalIcon}
          aria-label={`Line ${lineNumber} actions`}
          variant="invisible"
          size="small"
          tabStop={tabStop}
          className={clsx(
            styles.Target,
            styles.Actions,
            lane === 1 && styles.secondLane,
            gridRow !== undefined && styles.GridControl,
          )}
          style={gridRow === undefined ? undefined : {gridRow: `${gridRow} / span 1`}}
          data-edge={edge}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay {...controlProps} className={styles.Menu}>
        {/* The caller's range label replaces Primer's default trigger label. */}
        <ActionList aria-label={menuLabel} aria-labelledby={undefined}>
          <ActionList.Group>
            <ActionList.GroupHeading>{menuLabel}</ActionList.GroupHeading>
            {actions.map(action =>
              action.kind === 'link' ? (
                <ActionList.LinkItem key={action.id} href={action.href}>
                  {action.label}
                </ActionList.LinkItem>
              ) : (
                <ActionList.Item
                  key={action.id}
                  disabled={action.kind === 'unavailable'}
                  inactiveText={action.kind === 'unavailable' ? action.reason : undefined}
                  onSelect={action.kind === 'unavailable' ? undefined : action.onSelect}
                >
                  {action.label}
                  {action.kind === 'fixture' && <ActionList.Description>Fixture intent only</ActionList.Description>}
                </ActionList.Item>
              ),
            )}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function GutterAnnotations({
  lineNumber,
  tabStop,
  annotations,
  annotationOpen = false,
  onAnnotationOpenChange,
  gridRow,
  showLineActions,
  controlProps,
}: Pick<
  TaillightsGutterRowProps,
  'lineNumber' | 'tabStop' | 'annotationOpen' | 'onAnnotationOpenChange' | 'gridRow' | 'showLineActions'
> & {
  annotations: readonly TaillightsGutterAnnotation[]
  controlProps?: GutterControlProps
}) {
  const annotationRef = useRef<HTMLButtonElement>(null)
  const annotationId = useId()
  return (
    <AnchoredOverlay
      open={annotationOpen}
      anchorRef={annotationRef}
      onOpen={() => onAnnotationOpenChange(lineNumber, true)}
      onClose={() => onAnnotationOpenChange(lineNumber, false)}
      renderAnchor={props => (
        <IconButton
          {...props}
          {...controlProps}
          icon={AlertIcon}
          aria-haspopup="dialog"
          aria-label={`Line ${lineNumber} annotations: ${annotations.map(item => (item.kind === 'unicode' ? 'Hidden Unicode' : 'CODEOWNERS error')).join(', ')}`}
          variant="invisible"
          size="small"
          tabIndex={tabStop ? 0 : -1}
          className={clsx(styles.Target, styles.Annotation, gridRow !== undefined && styles.GridControl)}
          style={gridRow === undefined ? undefined : {gridRow: `${gridRow} / span 1`}}
          data-line-actions={showLineActions}
        />
      )}
      overlayProps={{...controlProps, role: 'dialog', 'aria-labelledby': annotationId}}
      className={styles.Details}
      preventOverflow={false}
    >
      <Heading as="h3" id={annotationId} className={styles.DetailsHeading}>
        Line {lineNumber} annotations
      </Heading>
      {annotations.map(item => (
        <Text as="p" key={item.kind}>
          <strong>{item.kind === 'unicode' ? 'Hidden Unicode' : 'CODEOWNERS error'}: </strong>
          {item.message}
        </Text>
      ))}
      <Button onClick={() => onAnnotationOpenChange(lineNumber, false)}>Close annotations</Button>
    </AnchoredOverlay>
  )
}
